import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import AdminNav from "../components/AdminNav";
import { adminService } from "../services/adminService";
import FormModal from "../components/FormModal";
import { toast } from "sonner";

export default function AdminDevicesPage() {
  const [devices, setDevices] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ deviceId: "", name: "", ownerUid: "", isActive: true });

  async function load() {
    setError("");
    const [d, u] = await Promise.all([adminService.getDevices(), adminService.getUsers()]);
    if (!d.ok) {
      const msg = d.error || "Failed to load devices";
      setError(msg);
      toast.error(msg);
      return;
    }
    if (!u.ok) {
      const msg = u.error || "Failed to load users";
      setError(msg);
      toast.error(msg);
      return;
    }
    setDevices(d.data?.items || []);
    setUsers(u.data?.items || []);
  }
  useEffect(() => { load(); }, []);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const n = String(u.displayName || "").toLowerCase();
      return n.includes(q);
    });
  }, [users, userSearch]);

  const filtered = useMemo(() => {
    if (!selectedUser) return devices;
    const selected = users.find((u) => u.uid === selectedUser);
    const ownedSet = new Set(selected?.deviceIds || []);
    const fromRegistry = devices.filter((d) => d.ownerUid === selectedUser || ownedSet.has(d.deviceId));
    const existingIds = new Set(fromRegistry.map((d) => d.deviceId));
    const missingFromRegistry = Object.entries(selected?.devices || {})
      .filter(([deviceId]) => !existingIds.has(deviceId))
      .map(([deviceId, info]) => ({
        deviceId,
        ownerUid: selectedUser,
        name: info?.name || "",
        isActive: true,
        fromUserMap: true,
      }));
    return [...fromRegistry, ...missingFromRegistry];
  }, [devices, selectedUser, users]);

  const selectedUserLabel = useMemo(() => {
    if (!selectedUser) return "All users";
    const u = users.find((x) => x.uid === selectedUser);
    return u?.displayName || "Unnamed user";
  }, [selectedUser, users]);

  return (
    <PageShell>
      <PageHero eyebrow="Admin" title="Devices" highlight="management." description="Filter devices by user, edit settings, and remove any device." />
      <PageSection variant="content">
        <div className="mx-auto max-w-7xl grid gap-6">
          <AdminNav />
          {error ? <Card><CardContent className="py-4 text-sm text-destructive">{error}</CardContent></Card> : null}
          <div><Button onClick={() => setAddOpen(true)}>Add Device</Button></div>
          <Card>
            <CardContent className="py-4 pt-4">
              <div className="relative">
                <button
                  type="button"
                  className="h-11 w-full px-4 rounded-xl border border-border bg-background text-sm flex items-center justify-between hover:border-accent/50"
                  onClick={() => setDropdownOpen((v) => !v)}
                >
                  <span>{selectedUserLabel}</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </button>
                {dropdownOpen ? (
                  <div className="absolute z-20 mt-2 w-full rounded-xl border border-border bg-card shadow-elevated p-2">
                    <div className="relative mb-2">
                      <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        className="h-10 w-full pl-9 pr-3 rounded-lg border border-border bg-background text-sm"
                        placeholder="Search user in dropdown"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                      />
                    </div>
                    <div className="max-h-56 overflow-auto grid gap-1">
                      <button type="button" className="text-left px-3 py-2 rounded-lg text-sm hover:bg-secondary/60" onClick={() => { setSelectedUser(""); setDropdownOpen(false); }}>
                        All users
                      </button>
                      {filteredUsers.map((u) => (
                        <button
                          key={u.uid}
                          type="button"
                          className="text-left px-3 py-2 rounded-lg text-sm hover:bg-secondary/60"
                          onClick={() => { setSelectedUser(u.uid); setDropdownOpen(false); }}
                        >
                          {u.displayName || "Unnamed user"}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </CardContent>
          </Card>
          {filtered.map((d) => (
            <Card key={d.deviceId} className="shadow-soft">
              <CardHeader><CardTitle>{d.name || d.deviceId}</CardTitle></CardHeader>
              <CardContent className="grid gap-3">
                <div className="text-xs text-muted-foreground">Device: {d.deviceId}</div>
                <div className="text-xs text-muted-foreground">Owner: {d.ownerUid || "-"}</div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      const cfg = await adminService.getDeviceConfig(d.deviceId);
                      setEditing({
                        ...d,
                        ssid: cfg.ok ? (cfg.data?.item?.ssid || "") : "",
                        password: cfg.ok ? (cfg.data?.item?.password || "") : "",
                      });
                      setEditOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        const r = await adminService.deleteDevice(d.deviceId);
                        if (!r.ok) {
                          setError(r.error);
                          toast.error(r.error || "Delete failed");
                        } else {
                          toast.success("Device removed");
                          await load();
                        }
                      }}
                    >
                      Remove device
                    </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {!filtered.length ? <Card><CardContent className="py-6 text-sm text-muted-foreground">No devices for selected user.</CardContent></Card> : null}
        </div>
      </PageSection>
      <FormModal open={addOpen} title="Add Device" onClose={() => setAddOpen(false)}>
        <div className="grid gap-2">
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Device ID" value={draft.deviceId} onChange={(e) => setDraft((p) => ({ ...p, deviceId: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Name" value={draft.name} onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Owner UID" value={draft.ownerUid} onChange={(e) => setDraft((p) => ({ ...p, ownerUid: e.target.value }))} />
          <div>
            <Button
              size="sm"
              onClick={async () => {
                if (!draft.deviceId.trim()) return setError("Device ID required");
                const id = draft.deviceId.trim();
                const prov = await adminService.provisionDevicePool({
                  deviceIds: [id],
                  batchLabel: "admin",
                });
                if (!prov.ok) {
                  setError(prov.error);
                  toast.error(prov.error || "Provision failed");
                  return;
                }
                if (draft.ownerUid.trim()) {
                  const r = await adminService.reassignDevice(id, draft.ownerUid.trim());
                  if (!r.ok) {
                    setError(r.error);
                    toast.error(r.error || "Assign owner failed");
                    return;
                  }
                }
                if (draft.name.trim()) {
                  await adminService.updateDevice(id, { name: draft.name.trim() });
                }
                toast.success("Device ID registered in pool");
                setAddOpen(false);
                setDraft({ deviceId: "", name: "", ownerUid: "", isActive: true });
                await load();
              }}
            >
              Save Device
            </Button>
          </div>
        </div>
      </FormModal>
      <FormModal open={editOpen} title="Edit Device" onClose={() => setEditOpen(false)}>
        <div className="grid gap-2">
          <div className="text-xs text-muted-foreground">Device: {editing?.deviceId}</div>
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Device name" value={editing?.name || ""} onChange={(e) => setEditing((p) => ({ ...(p || {}), name: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Owner UID" value={editing?.ownerUid || ""} onChange={(e) => setEditing((p) => ({ ...(p || {}), ownerUid: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Wi-Fi SSID" value={editing?.ssid || ""} onChange={(e) => setEditing((p) => ({ ...(p || {}), ssid: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Wi-Fi Password" value={editing?.password || ""} onChange={(e) => setEditing((p) => ({ ...(p || {}), password: e.target.value }))} />
          <label className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
            <input type="checkbox" checked={editing?.isActive !== false} onChange={(e) => setEditing((p) => ({ ...(p || {}), isActive: e.target.checked }))} />
            Device active
          </label>
          <div>
            <Button
              size="sm"
              onClick={async () => {
                if (!editing?.deviceId) return;
                const r = await adminService.updateDevice(editing.deviceId, {
                  name: editing.name,
                  ownerUid: editing.ownerUid,
                  isActive: editing.isActive !== false,
                  ssid: editing.ssid || "",
                  password: editing.password || "",
                });
                if (!r.ok) {
                  setError(r.error);
                  toast.error(r.error || "Update failed");
                } else {
                  toast.success("Device updated");
                  setEditOpen(false);
                  await load();
                }
              }}
            >
              Update Device
            </Button>
          </div>
        </div>
      </FormModal>
    </PageShell>
  );
}

